// UnassignedPlayersPanel — admin maintenance UI for orphan players.
//
// ════════════════════════════════════════════════════════════════════
// CONTEXT (2026-05-18 — Phase C3 of the orphan-prevention deploy)
// ════════════════════════════════════════════════════════════════════
//
// Under the locked architecture every player MUST be owned by an
// Agent. Migration 087 backfilled legacy NULL-agent_id rows via the
// 4-level geographic cascade; any row the cascade couldn't resolve
// (missing geo data, no eligible agent in their nation, etc.) is
// left NULL and surfaced here for manual admin handling. Going
// forward, admin create + reassign handlers refuse to mint orphans,
// so this list should drain to zero and stay there.
//
// What admin can do per row:
//   • "Auto-assign by geo"  — POST /admin/users/:id/owner with
//                              { auto_assign_by_geo: true }. Re-runs
//                              the 5-level cascade and uses whatever
//                              it returns. Useful when migration 087's
//                              SQL-only 4-level pass missed (e.g. the
//                              L1 radius level which needs JS).
//   • "Assign to agent…"    — opens a small picker that takes a UAID
//                              and resolves via /admin/agents/lookup,
//                              then POSTs the explicit reassignment.
//
// The panel is read-only for any non-admin role (the backend's
// /admin/users?unassigned=1 endpoint is admin-only anyway; the
// component still gates the actions to be safe).

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type UnassignedRow = {
  id: number;
  username: string;
  email: string;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  status: string;
  created_at: string;
  agent_id: number | null;     // always null in this list
  agent_username: string | null; // always null in this list
  // Geo signals — surfaced from the SELECT so admin can SEE why the
  // cascade missed (e.g. all geo NULL → cascade had nothing to match).
  state_id: number | null;
  district_id: number | null;
  area_id: number | null;
  nation: string | null;
};

type ListResp = { rows: UnassignedRow[]; total: number; limit: number; offset: number };

function displayName(first: string | null | undefined, last: string | null | undefined): string {
  const f = (first || '').trim();
  const l = (last  || '').trim();
  if (f && l) return `${f} ${l}`;
  if (f) return f;
  if (l) return l;
  return '—';
}

const fmtDate = (s: string) =>
  new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

export default function UnassignedPlayersPanel() {
  const auth = useAdminAuth();
  const isAdmin = auth.admin?.role === 'admin';
  const [rows, setRows] = useState<UnassignedRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [assignTarget, setAssignTarget] = useState<{ row: UnassignedRow; uaid: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminApi.get<ListResp>('/admin/users?unassigned=1&limit=50');
      setRows(r.rows);
      setTotal(r.total);
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function autoAssign(row: UnassignedRow) {
    if (!confirm(`Auto-assign player @${row.username} via geographic cascade?`)) return;
    setBusyId(row.id); setErr(null);
    try {
      const r = await adminApi.post<{ resolved_via: string }>(
        `/admin/users/${row.id}/owner`,
        { auto_assign_by_geo: true },
      );
      setToast(`Assigned via ${r.resolved_via}`);
      await load();
    } catch (e) {
      setErr((e as ApiError).message || 'Auto-assign failed');
    } finally { setBusyId(null); }
  }

  async function assignExplicit() {
    if (!assignTarget) return;
    const { row, uaid } = assignTarget;
    if (!uaid.trim()) {
      setErr('Enter a UAID to assign');
      return;
    }
    setBusyId(row.id); setErr(null);
    try {
      // Resolve UAID → agent id via the existing lookup endpoint.
      const lookup = await adminApi.get<{ agent: { id: number } }>(`/admin/agents/lookup?uaid=${encodeURIComponent(uaid.trim())}`);
      // Then reassign.
      await adminApi.post(`/admin/users/${row.id}/owner`, { agent_id: lookup.agent.id });
      setToast(`Assigned @${row.username} to UAID ${uaid.trim().toUpperCase()}`);
      setAssignTarget(null);
      await load();
    } catch (e) {
      setErr((e as ApiError).message || 'Assignment failed');
    } finally { setBusyId(null); }
  }

  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-500/[0.06] dark:to-orange-500/[0.06] ring-1 ring-inset ring-amber-200/60 dark:ring-amber-400/30 p-4 sm:p-5"
      >
        <div className="flex items-start gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30 shrink-0">
            <Icon name="warning" size={22} filled />
          </span>
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-neutral-900 dark:text-white">
              Unassigned players · {total}
            </h2>
            <p className="text-[12px] text-arena-700 dark:text-white/65 mt-0.5">
              Legacy players whose agent owner couldn't be auto-resolved by the geographic cascade (migration 087).
              Under the locked architecture every player MUST belong to an agent — these rows can't recharge or
              withdraw until you assign one. New accounts created after 2026-05-18 cannot land here; the
              admin-create and reassign handlers refuse to mint orphans.
            </p>
          </div>
        </div>
      </motion.div>

      {err && (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-200 px-4 py-3 text-[13px]">{err}</div>
      )}
      {toast && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-200 px-4 py-3 text-[13px]">{toast}</div>
      )}

      {!loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-10 text-center">
          <Icon name="check_circle" size={36} className="text-emerald-500 dark:text-emerald-300 mx-auto mb-3" filled />
          <div className="text-[14px] font-semibold text-neutral-900 dark:text-white">No unassigned players</div>
          <div className="text-[12px] text-arena-600 dark:text-neon-200/65 mt-1">
            Every player on the platform is owned by an active agent.
          </div>
        </div>
      )}
      {loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-10 text-center text-[13px] text-arena-600 dark:text-neon-200/70">
          Loading…
        </div>
      )}

      {rows.length > 0 && (
        <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((row) => {
            const hasGeo = row.state_id != null || row.district_id != null || row.area_id != null;
            const busy = busyId === row.id;
            return (
              <li key={row.id}>
                <motion.div
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl p-4 bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40"
                >
                  <div className="flex items-start gap-3">
                    <div className="grid place-items-center w-9 h-9 rounded-full bg-arena-100 text-arena-700 dark:bg-arena-700/60 dark:text-neon-200/75 ring-1 ring-arena-200 dark:ring-arena-600/40 shrink-0">
                      <Icon name="person_off" size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link to={`/people/players/${row.id}`} className="text-[13.5px] font-semibold text-neutral-900 dark:text-white truncate hover:underline block">
                        {displayName(row.first_name, row.last_name)}
                      </Link>
                      <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate">
                        @{row.username} · {row.phone || row.email || `#${row.id}`}
                      </div>
                      <div className="text-[10.5px] text-arena-500 dark:text-neon-200/45 mt-0.5">created {fmtDate(row.created_at)}</div>
                      <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold
                        ${hasGeo
                          ? 'bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-200/60 dark:bg-amber-400/15 dark:text-amber-200 dark:ring-amber-400/30'
                          : 'bg-rose-100 text-rose-800 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-400/15 dark:text-rose-200 dark:ring-rose-400/30'}`}>
                        <Icon name={hasGeo ? 'location_on' : 'location_off'} size={11} />
                        {hasGeo
                          ? `Geo: state=${row.state_id ?? '–'} · district=${row.district_id ?? '–'} · area=${row.area_id ?? '–'}`
                          : 'No geographic data on file'}
                      </div>
                    </div>
                  </div>

                  {isAdmin && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => autoAssign(row)}
                        disabled={busy || !hasGeo}
                        title={hasGeo ? 'Re-run the 5-level geographic cascade' : 'No geo data — manual assignment required'}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold
                                   bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white
                                   disabled:bg-arena-200 disabled:text-arena-500 dark:text-neon-200/60 disabled:cursor-not-allowed
                                   dark:disabled:bg-arena-700 dark:disabled:text-neon-200/40
                                   transition active:scale-95"
                      >
                        <Icon name={busy ? 'progress_activity' : 'travel_explore'} size={14} className={busy ? 'animate-spin' : ''} />
                        Auto-assign by geo
                      </button>
                      <button
                        onClick={() => setAssignTarget({ row, uaid: '' })}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold
                                   bg-white text-violet-700 ring-1 ring-inset ring-violet-200/60 hover:bg-violet-50
                                   dark:bg-arena-700/40 dark:text-violet-300 dark:ring-violet-400/30 dark:hover:bg-violet-400/10
                                   transition active:scale-95"
                      >
                        <Icon name="person_add" size={14} />
                        Assign to agent…
                      </button>
                    </div>
                  )}
                </motion.div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Modal-ish UAID picker */}
      {assignTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-start sm:place-items-center px-4 overflow-y-auto"
             onClick={() => setAssignTarget(null)}>
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-sm rounded-2xl bg-white dark:bg-arena-700/95 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-5 space-y-3 shadow-xl">
            <div className="flex items-start gap-3">
              <span className="grid place-items-center w-9 h-9 rounded-xl bg-violet-50 text-violet-700 ring-1 ring-violet-100 dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
                <Icon name="person_add" size={18} filled />
              </span>
              <div className="min-w-0">
                <h3 className="text-[14px] font-semibold text-neutral-900 dark:text-white">
                  Assign @{assignTarget.row.username}
                </h3>
                <p className="text-[11px] text-arena-600 dark:text-neon-200/65">
                  Enter the destination agent's Unique Agent ID (UAID).
                </p>
              </div>
            </div>
            <input
              autoFocus
              value={assignTarget.uaid}
              onChange={(e) => setAssignTarget({ ...assignTarget, uaid: e.target.value.toUpperCase() })}
              maxLength={12}
              placeholder="e.g. AB7CDE34"
              className="w-full rounded-xl px-3 py-2 text-[14px] font-mono uppercase tracking-wider
                         bg-white dark:bg-arena-800/60
                         ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                         text-neutral-900 dark:text-white
                         outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/30"
            />
            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={() => setAssignTarget(null)}
                      className="px-3 py-1.5 rounded-full text-[12px] font-semibold text-arena-700 dark:text-neon-200/70 hover:bg-arena-100 dark:hover:bg-arena-700/60">
                Cancel
              </button>
              <button onClick={assignExplicit} disabled={busyId === assignTarget.row.id}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-bold
                                 bg-violet-600 hover:bg-violet-500 active:bg-violet-700 text-white
                                 transition active:scale-95 disabled:opacity-50">
                <Icon name={busyId === assignTarget.row.id ? 'progress_activity' : 'check'} size={14}
                      className={busyId === assignTarget.row.id ? 'animate-spin' : ''} />
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
